/**
 * Consult Door B — MOCK booking provider (design-QA harness only).
 *
 * Generates the OWNER'S REAL agreed availability so the preview is honest until
 * Mack's live backend deploys. IST windows (Asia/Kolkata, no DST), 30-min slots,
 * Mon–Sat (Sunday off), next 7 days, 2-hour min-notice:
 *
 *     08:00–09:00 · 11:00–12:00 · 15:00–17:00 · 18:30–19:30 · 20:00–21:30
 *
 * Variant slugs (mirrors the two backend variant types Mack is seeding):
 *   · discovery        → all five windows
 *   · discovery-india  → 11:00–12:00 + 18:30–19:30 only
 *   · discovery-intl   → 08:00–09:00 + 15:00–17:00 + 20:00–21:30 only
 *
 * Selected ONLY via `?preview=1` / the harness — never the live route. Writes
 * nothing; not a production code path. createBooking → confirmed (with a sample
 * Meet link + reschedule token); one slot returns `slot_taken` on first pick so
 * the "slot just taken" recovery is reachable in QA.
 */
import {
  DISCOVERY_TYPE,
  type BookingApi,
  type PublicSlot,
} from './publicBookingApi';

/** IST minute-of-day windows [startMin, endMin]. */
const WINDOWS_ALL: Array<[number, number]> = [
  [8 * 60, 9 * 60], //             08:00–09:00
  [11 * 60, 12 * 60], //           11:00–12:00
  [15 * 60, 17 * 60], //           15:00–17:00
  [18 * 60 + 30, 19 * 60 + 30], // 18:30–19:30
  [20 * 60, 21 * 60 + 30], //      20:00–21:30
];
const WINDOWS_INDIA: Array<[number, number]> = [
  [11 * 60, 12 * 60],
  [18 * 60 + 30, 19 * 60 + 30],
];
const WINDOWS_INTL: Array<[number, number]> = [
  [8 * 60, 9 * 60],
  [15 * 60, 17 * 60],
  [20 * 60, 21 * 60 + 30],
];

function windowsForSlug(slug?: string): Array<[number, number]> {
  if (slug === 'discovery-india') return WINDOWS_INDIA;
  if (slug === 'discovery-intl') return WINDOWS_INTL;
  return WINDOWS_ALL;
}

const IST_OFFSET_MS = 330 * 60_000; // +05:30, fixed (no DST)
const SLOT_MIN = 30;
const DOW_MAP: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/** The IST calendar Y-M-D + weekday for an instant. */
function istDateParts(at: Date): { y: number; m: number; d: number; dow: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).formatToParts(at);
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;
  return {
    y: parseInt(p.year, 10),
    m: parseInt(p.month, 10),
    d: parseInt(p.day, 10),
    dow: DOW_MAP[p.weekday] ?? 0,
  };
}

/** Real IST-anchored open slots for the next `days` days. Reads `now`. */
function generateSlots(slug: string | undefined, days = 7): PublicSlot[] {
  const windows = windowsForSlug(slug);
  const now = Date.now();
  const minStartMs = now + 2 * 60 * 60 * 1000; // 2-hour min-notice
  const out: PublicSlot[] = [];
  for (let dayOffset = 0; dayOffset < days; dayOffset++) {
    const { y, m, d, dow } = istDateParts(new Date(now + dayOffset * 86_400_000));
    if (dow === 0) continue; // Sunday off
    for (const [ws, we] of windows) {
      for (let s = ws; s + SLOT_MIN <= we; s += SLOT_MIN) {
        // IST wall time → UTC instant (IST has no DST → fixed subtraction).
        const startMs =
          Date.UTC(y, m - 1, d, Math.floor(s / 60), s % 60) - IST_OFFSET_MS;
        if (startMs < minStartMs) continue;
        out.push({
          startISO: new Date(startMs).toISOString(),
          endISO: new Date(startMs + SLOT_MIN * 60_000).toISOString(),
        });
      }
    }
  }
  out.sort((a, b) => a.startISO.localeCompare(b.startISO));
  return out;
}

export function makeMockBookingApi(): BookingApi {
  let raceStartISO: string | null = null;
  let raceConsumed = false;
  let lastSlots: PublicSlot[] = [];

  const freshAfterTaken = (): PublicSlot[] =>
    lastSlots.filter((s) => s.startISO !== raceStartISO).slice(0, 4);

  return {
    isPreview: true,

    async getAvailability(q) {
      await new Promise((r) => setTimeout(r, 220)); // visible loading state in QA
      const slots = generateSlots(q.typeSlug, q.pickerWindowDays ?? 7);
      lastSlots = slots;
      // Make the 2nd slot the one that "just gets taken" so state 6 is reachable.
      raceStartISO = slots.length > 1 ? slots[1].startISO : null;
      return {
        kind: 'ok',
        type: { ...DISCOVERY_TYPE, slug: q.typeSlug ?? DISCOVERY_TYPE.slug },
        slots,
        tz: q.clientTz,
        prefill: q.token
          ? { firstName: 'Sumit', projectType: 'New home', country: 'India' }
          : null,
      };
    },

    async createBooking(input) {
      await new Promise((r) => setTimeout(r, 260));
      if (!raceConsumed && input.slotStartISO === raceStartISO) {
        raceConsumed = true;
        return { kind: 'slot_taken', freshSlots: freshAfterTaken() };
      }
      return {
        kind: 'ok',
        booking: {
          consultationId: 'CS-PREVIEW-0001',
          status: 'confirmed',
          startISO: input.slotStartISO,
          endISO: input.slotEndISO,
          meetUrl: 'https://meet.google.com/aayatan-preview',
          rescheduleToken: 'preview-reschedule-token',
          // honest reality today: email always-on; WA templates PENDING Meta
          // approval so the WA path self-skips → not yet sent.
          sentChannels: { email: true, whatsapp: false },
        },
      };
    },

    async reschedule(_token, newSlotStartISO, newSlotEndISO) {
      await new Promise((r) => setTimeout(r, 240));
      return {
        kind: 'ok',
        booking: {
          consultationId: 'CS-PREVIEW-0001',
          status: 'confirmed',
          startISO: newSlotStartISO,
          endISO: newSlotEndISO,
          meetUrl: 'https://meet.google.com/aayatan-preview',
          rescheduleToken: 'preview-reschedule-token',
          sentChannels: { email: true, whatsapp: false },
        },
      };
    },

    async cancel() {
      await new Promise((r) => setTimeout(r, 200));
      return { kind: 'ok' };
    },
  };
}
